import { ChangeDetectionStrategy, Component, DestroyRef, HostBinding, OnInit, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { selectMemberProfile } from '@lib/members/data-access';
import {
  PrescriptionsFilters,
  clearPrescriptions,
  clearTargetPrescription,
  fetchPrescriptions,
  incrementPrescriptionsPage,
  selectPrescriptionDrafts,
  selectPrescriptionsCount,
  selectPrescriptionsFilters,
  selectPrescriptionsGuaranteesFetching,
  selectPrescriptionsHasNextPage,
  selectPrescriptionsHasPreviousPage,
  selectPrescriptionsInitRequired,
  selectPrescriptionsLoading,
  selectPrescriptionsPage,
  selectPrescriptionsPageSize,
  setPrescriptionsInitRequired,
  setPrescriptionsSearchFilter,
  setTargetPrescription,
} from '@lib/prescriptions/data-access';
import {
  GenericServiceValue,
  GuaranteeApplicationSearchModel,
  GuaranteeApplicationStatus,
  PersonalDoctorValue,
  PharmacologicalTreatmentValue,
  PrescriptionFormValue,
  PrescriptionStatus,
} from '@lib/prescriptions/data-access';
import { GuaranteesListComponent } from '@lib/prescriptions/feature';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { debounceTime, map } from 'rxjs';

import { MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';

import { ProcessPrescriptionDraftsModalComponent } from '@hm/ui/modals/prescriptions/process-prescription-drafts-modal/process-prescription-drafts-modal.component';

import { MemberProfile } from '@lib/core/models/member.interface';
import { VisitStatusTypes } from '@lib/core/models/visit.interface';

import { InfoBadgeComponent } from '@lib/shared-ui/commons/info-badge/info-badge.component';
import { SpinLoadingComponent } from '@lib/shared-ui/commons/spin-loading/spin-loading.component';
import { Diagnosis } from '@lib/shared-ui/core/models/diagnoses.interface';
import { InfiniteScrollDirective } from '@lib/shared-ui/directives/infinite-scroll.directive';
import { LoaderComponent } from '@lib/shared-ui/layout/loader/loader.component';

// TODO: adjust in scope of filters implementation
interface GuaranteeFiltersControls {
  search?: FormControl<string>;
  date?: FormControl<Date | null>;
  guaranteeStatus?: FormControl<GuaranteeApplicationStatus | null>;
  prescriptionStatus?: FormControl<PrescriptionStatus | null>;
  appointmentStatus?: FormControl<VisitStatusTypes | null>;
  type?: FormControl<string | null>;
  diagnosis?: FormControl<Diagnosis | null>;
}

@Component({
  selector: 'members-guarantees',
  standalone: true,
  imports: [
    MatIcon,
    MatIconButton,
    MatInput,
    ReactiveFormsModule,
    InfiniteScrollDirective,
    LoaderComponent,
    SpinLoadingComponent,
    InfoBadgeComponent,
    MatFormField,
    MatSuffix,
    MatLabel,
    TranslatePipe,
    GuaranteesListComponent,
  ],
  templateUrl: './member-guarantees.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MemberGuaranteesComponent implements OnInit {
  @HostBinding('class') class = 'h-full';

  allowCreateGuarantee = input<boolean>(true);

  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);

  protected readonly memberProfile = this.store.selectSignal<MemberProfile>(selectMemberProfile);
  protected readonly prescriptionsInitRequired = this.store.selectSignal<boolean>(selectPrescriptionsInitRequired);
  protected readonly guaranteesFilters = this.store.selectSignal<PrescriptionsFilters>(selectPrescriptionsFilters);
  protected readonly count = this.store.selectSignal<number>(selectPrescriptionsCount);
  protected readonly page = this.store.selectSignal<number>(selectPrescriptionsPage);
  protected readonly pageSize = this.store.selectSignal<number>(selectPrescriptionsPageSize);
  protected readonly hasNextPage = this.store.selectSignal<boolean>(selectPrescriptionsHasNextPage);
  protected readonly hasPreviousPage = this.store.selectSignal<boolean>(selectPrescriptionsHasPreviousPage);
  protected readonly draftPrescriptions =
    this.store.selectSignal<PrescriptionFormValue<PharmacologicalTreatmentValue | GenericServiceValue | PersonalDoctorValue>[]>(selectPrescriptionDrafts);
  protected readonly loading = this.store.selectSignal<boolean>(selectPrescriptionsLoading);
  protected readonly guaranteesFetching = this.store.selectSignal<boolean>(selectPrescriptionsGuaranteesFetching);

  protected guaranteesFilterForm!: FormGroup<GuaranteeFiltersControls>;

  protected get fc() {
    return this.guaranteesFilterForm?.controls;
  }

  ngOnInit(): void {
    this.initFiltersForm();
    this.initFiltersWatcher();
    this.initPrescriptionsList();
    this.checkForPrescriptionHighlighting();
  }

  private initFiltersForm() {
    this.guaranteesFilterForm = this.fb.group<GuaranteeFiltersControls>({
      search: this.fb.nonNullable.control(this.guaranteesFilters().search),
    });
  }

  private initFiltersWatcher() {
    this.guaranteesFilterForm.valueChanges
      .pipe(
        debounceTime(300),
        map(formValue => ({
          search: formValue.search.trim(),
        })),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(filters => {
        this.store.dispatch(setPrescriptionsSearchFilter(filters));
        this.store.dispatch(clearPrescriptions());
        this.fetchPrescriptions();
      });
  }

  private initPrescriptionsList() {
    if (this.prescriptionsInitRequired()) {
      this.fetchPrescriptions();
      this.store.dispatch(setPrescriptionsInitRequired({ initRequired: false }));
    }
  }

  private fetchPrescriptions(payload?: Partial<GuaranteeApplicationSearchModel>) {
    let localPayload: Partial<GuaranteeApplicationSearchModel>;
    if (!payload) {
      localPayload = {
        prescriptionName: this.guaranteesFilters().search,
        page: this.page(),
        pageSize: this.pageSize(),
        userId: this.memberProfile().userId,
        sortBy: 'last_update_date',
        sortDirection: 'desc',
      };
    } else {
      localPayload = payload;
    }
    this.store.dispatch(fetchPrescriptions(localPayload));
  }

  protected fetchNextPage() {
    if (this.hasNextPage()) {
      this.store.dispatch(incrementPrescriptionsPage());
      this.fetchPrescriptions();
    }
  }

  protected clearSearchControl() {
    this.fc.search.setValue('');
  }

  protected navigateToCreateGuarantee() {
    return this.router.navigate([`members/${this.memberProfile().id}/guarantees/create`]);
  }

  protected processDraftPrescriptions() {
    this.dialog.open(ProcessPrescriptionDraftsModalComponent, {
      autoFocus: false,
      maxHeight: '90vh',
      maxWidth: '80vw',
      width: '1024px',
    });
  }

  protected guaranteeUpdated(event: boolean) {
    if (event) {
      const payload: Partial<GuaranteeApplicationSearchModel> = {
        prescriptionName: this.guaranteesFilters().search,
        page: 1,
        pageSize: this.page() * this.pageSize(),
        userId: this.memberProfile().userId,
        sortBy: 'lastUpdateDate',
        sortDirection: 'desc',
      };
      this.store.dispatch(clearPrescriptions());
      this.fetchPrescriptions(payload);
    }
  }

  private checkForPrescriptionHighlighting(): void {
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      if (params['prescription']) {
        this.store.dispatch(clearTargetPrescription());

        this.store.dispatch(setTargetPrescription({ prescriptionId: params['prescription'] }));
      } else this.store.dispatch(clearTargetPrescription());
    });
  }
}
